import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Users,
  Plus,
  Search,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  Trash2,
  Settings,
  Loader2,
  UserPlus,
  CheckCircle,
  X,
  Stethoscope
} from 'lucide-react';
import { toast } from 'sonner';

const professionalTypes = [
  { value: 'doctor', label: 'Médecin' },
  { value: 'nurse', label: 'Infirmier(ère)' },
  { value: 'midwife', label: 'Sage-femme' },
  { value: 'physiotherapist', label: 'Kinésithérapeute' },
  { value: 'dentist', label: 'Dentiste' },
  { value: 'lab_technician', label: 'Technicien de labo' },
  { value: 'radiologist', label: 'Radiologue' },
  { value: 'other', label: 'Autre' },
];

const dayLabels = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
  saturday: 'Samedi',
  sunday: 'Dimanche'
};

const FacilityProfessionals = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [professionals, setProfessionals] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [creating, setCreating] = useState(false);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [activeTab, setActiveTab] = useState('search');
  
  const [inviteForm, setInviteForm] = useState({
    email: '',
    professional_type: '',
    specialty: '',
    message: ''
  });

  const [createForm, setCreateForm] = useState({
    full_name: '',
    email: '',
    professional_type: '',
    specialty: '',
    license_number: '',
    phone: ''
  });
  
  const [availabilityForm, setAvailabilityForm] = useState({
    consultation_slots: {},
    video_slots: {},
    consultation_duration: 30,
    video_duration: 20
  });

  useEffect(() => {
    loadProfessionals();
  }, []);

  const loadProfessionals = async () => {
    try {
      const response = await api.get('/facility-professionals');
      setProfessionals(response.data.professionals || []);
    } catch (error) {
      console.error('Error loading professionals:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const searchProfessionals = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await api.get('/facility-professionals/search', {
        params: { query: searchQuery }
      });
      setSearchResults(response.data.professionals || []);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.email) {
      toast.error('Veuillez saisir un email');
      return;
    }
    
    setInviting(true);
    try {
      await api.post('/facility-professionals/invite', inviteForm);
      toast.success('Invitation envoyée par email');
      setShowInviteModal(false);
      setInviteForm({ email: '', professional_type: '', specialty: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setInviting(false);
    }
  };

  const handleCreateDirect = async () => {
    if (!createForm.full_name || !createForm.email || !createForm.professional_type) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    
    setCreating(true);
    try {
      const response = await api.post('/facility-professionals/create-direct', createForm);
      toast.success(response.data.message);
      setShowCreateModal(false);
      setCreateForm({
        full_name: '',
        email: '',
        professional_type: '',
        specialty: '',
        license_number: '',
        phone: ''
      });
      loadProfessionals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const handleAddProfessional = async (professionalId) => {
    try {
      await api.post(`/facility-professionals/add?professional_id=${professionalId}`);
      toast.success('Professionnel ajouté');
      loadProfessionals();
      setSearchResults([]);
      setSearchQuery('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleRemoveProfessional = async (affiliationId) => {
    if (!confirm('Êtes-vous sûr de vouloir retirer ce professionnel ?')) return;
    
    try {
      await api.delete(`/facility-professionals/${affiliationId}`);
      toast.success('Professionnel retiré');
      loadProfessionals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const openAvailabilityModal = (professional) => {
    setSelectedProfessional(professional);
    setAvailabilityForm({
      consultation_slots: professional.consultation_slots || {},
      video_slots: professional.video_slots || {},
      consultation_duration: professional.consultation_duration || 30,
      video_duration: professional.video_duration || 20
    });
    setShowAvailabilityModal(true);
  };

  const addTimeSlot = (slotType, day) => {
    const slotsKey = `${slotType}_slots`;
    const currentSlots = availabilityForm[slotsKey][day] || [];
    
    setAvailabilityForm(prev => ({
      ...prev,
      [slotsKey]: {
        ...prev[slotsKey],
        [day]: [...currentSlots, { start: '08:00', end: '12:00' }]
      }
    }));
  };

  const updateTimeSlot = (slotType, day, index, field, value) => {
    const slotsKey = `${slotType}_slots`;
    
    setAvailabilityForm(prev => ({
      ...prev,
      [slotsKey]: {
        ...prev[slotsKey],
        [day]: prev[slotsKey][day].map((slot, i) => 
          i === index ? { ...slot, [field]: value } : slot
        )
      }
    }));
  };

  const removeTimeSlot = (slotType, day, index) => {
    const slotsKey = `${slotType}_slots`;
    
    setAvailabilityForm(prev => ({
      ...prev,
      [slotsKey]: {
        ...prev[slotsKey],
        [day]: prev[slotsKey][day].filter((_, i) => i !== index)
      }
    }));
  };

  const saveAvailability = async () => {
    if (!selectedProfessional) return;
    
    setSavingAvailability(true);
    try {
      await api.put(`/facility-professionals/${selectedProfessional.id}/availability`, availabilityForm);
      toast.success('Disponibilités mises à jour');
      setShowAvailabilityModal(false);
      loadProfessionals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSavingAvailability(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="section-padding py-6" data-testid="facility-professionals-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Mon équipe
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Gérez les professionnels de votre établissement
            </p>
          </div>
          <Button
            onClick={() => setShowInviteModal(true)}
            className="bg-red-500 hover:bg-red-600 text-white"
            data-testid="invite-professional-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Inviter un professionnel
          </Button>
        </div>

        {/* Search existing professionals */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Search className="w-5 h-5" />
              Rechercher un professionnel
            </CardTitle>
            <CardDescription>
              Trouvez et ajoutez des professionnels inscrits sur DOCKHTA
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input
                placeholder="Nom ou email du professionnel..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchProfessionals()}
                className="flex-1"
              />
              <Button onClick={searchProfessionals} disabled={searching}>
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-4 space-y-2">
                {searchResults.map((pro) => (
                  <div key={pro.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={pro.picture} />
                        <AvatarFallback>{pro.full_name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{pro.full_name}</p>
                        <p className="text-sm text-slate-500">{pro.profile?.specialty || pro.professional_type}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleAddProfessional(pro.id)}
                      className="bg-emerald-500 hover:bg-emerald-600"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Professionnels affiliés ({professionals.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {professionals.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600">Aucun professionnel</h3>
                <p className="text-slate-500 text-sm mt-1">
                  Invitez des professionnels à rejoindre votre établissement
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {professionals.map((aff) => (
                  <div key={aff.id} className="flex items-center justify-between p-4 border rounded-xl hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={aff.professional?.picture} />
                        <AvatarFallback className="bg-red-100 text-red-600">
                          {aff.professional_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h3 className="font-semibold">{aff.professional_name}</h3>
                        <p className="text-sm text-slate-500">
                          {aff.profile?.specialty || aff.professional?.professional_type}
                        </p>
                        {aff.professional?.email && (
                          <p className="text-xs text-slate-400 flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {aff.professional.email}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Availability summary */}
                      <div className="text-right hidden md:block">
                        {Object.values(aff.consultation_slots || {}).some(slots => slots && slots.length > 0) ? (
                          <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Disponibilités configurées
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                            <Clock className="w-3 h-3 mr-1" />
                            Non configuré
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openAvailabilityModal(aff)}
                        >
                          <Calendar className="w-4 h-4 mr-1" />
                          Disponibilités
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleRemoveProfessional(aff.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Invite/Add Professional Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>Ajouter un professionnel</DialogTitle>
            <DialogDescription>
              Recherchez, invitez ou créez un compte professionnel
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col px-6">
            <TabsList className="grid grid-cols-3 mb-4">
              <TabsTrigger value="search" className="text-xs">
                <Search className="w-3 h-3 mr-1" />
                Rechercher
              </TabsTrigger>
              <TabsTrigger value="invite" className="text-xs">
                <Mail className="w-3 h-3 mr-1" />
                Inviter
              </TabsTrigger>
              <TabsTrigger value="create" className="text-xs">
                <UserPlus className="w-3 h-3 mr-1" />
                Créer
              </TabsTrigger>
            </TabsList>
            
            <div className="flex-1 overflow-y-auto pb-6">
              {/* Search Tab */}
              <TabsContent value="search" className="mt-0 space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Nom, spécialité, email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchProfessionals()}
                  />
                  <Button onClick={searchProfessionals} disabled={searching}>
                    {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  </Button>
                </div>
                
                {searchResults.length > 0 ? (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {searchResults.map((pro) => (
                      <div key={pro.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                            <Stethoscope className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{pro.full_name}</p>
                            <p className="text-xs text-slate-500">{pro.specialty || pro.professional_type}</p>
                          </div>
                        </div>
                        <Button size="sm" onClick={() => handleAddProfessional(pro.id)}>
                          <Plus className="w-4 h-4 mr-1" />
                          Ajouter
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : searchQuery && !searching ? (
                  <p className="text-sm text-slate-500 text-center py-4">
                    Aucun résultat. Essayez d&apos;inviter par email ou de créer un compte.
                  </p>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">
                    Recherchez des professionnels déjà inscrits sur DOCKHTA
                  </p>
                )}
              </TabsContent>
              
              {/* Invite by Email Tab */}
              <TabsContent value="invite" className="mt-0 space-y-4">
                <div>
                  <Label>Email du professionnel *</Label>
                  <Input
                    type="email"
                    placeholder="professionnel@email.com"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Type de professionnel</Label>
                  <Select
                    value={inviteForm.professional_type}
                    onValueChange={(v) => setInviteForm({ ...inviteForm, professional_type: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      {professionalTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Spécialité (optionnel)</Label>
                  <Input
                    placeholder="Ex: Cardiologie"
                    value={inviteForm.specialty}
                    onChange={(e) => setInviteForm({ ...inviteForm, specialty: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Message personnalisé (optionnel)</Label>
                  <Input
                    placeholder="Un message pour le professionnel..."
                    value={inviteForm.message}
                    onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })}
                    className="mt-1"
                  />
                </div>
                
                <Button onClick={handleInvite} disabled={inviting} className="w-full bg-[#1A237E] hover:bg-[#0D1642]">
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                  Envoyer l&apos;invitation par email
                </Button>
              </TabsContent>
              
              {/* Create Direct Tab */}
              <TabsContent value="create" className="mt-0 space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg mb-2">
                  <p className="text-xs text-blue-700">
                    Un compte sera créé avec un mot de passe temporaire envoyé par email.
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label>Nom complet *</Label>
                    <Input
                      placeholder="Dr. Jean Dupont"
                      value={createForm.full_name}
                      onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      placeholder="email@exemple.com"
                      value={createForm.email}
                      onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Type *</Label>
                    <Select
                      value={createForm.professional_type}
                      onValueChange={(v) => setCreateForm({ ...createForm, professional_type: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {professionalTypes.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Spécialité</Label>
                    <Input
                      placeholder="Cardiologie"
                      value={createForm.specialty}
                      onChange={(e) => setCreateForm({ ...createForm, specialty: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>N° Licence</Label>
                    <Input
                      placeholder="123456"
                      value={createForm.license_number}
                      onChange={(e) => setCreateForm({ ...createForm, license_number: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Téléphone</Label>
                    <Input
                      placeholder="6XX XX XX XX"
                      value={createForm.phone}
                      onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <Button onClick={handleCreateDirect} disabled={creating} className="w-full bg-[#DC2626] hover:bg-[#B91C1C]">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Créer le compte et ajouter
                </Button>
              </TabsContent>
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Availability Modal */}
      <Dialog open={showAvailabilityModal} onOpenChange={setShowAvailabilityModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Disponibilités - {selectedProfessional?.professional_name}</DialogTitle>
            <DialogDescription>
              Définissez les plages horaires de consultation
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="consultation" className="mt-4">
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="consultation">Consultation</TabsTrigger>
              <TabsTrigger value="video">Téléconsultation</TabsTrigger>
            </TabsList>
            
            <TabsContent value="consultation" className="mt-4">
              <div className="mb-4">
                <Label className="text-sm">Durée d&apos;une consultation (min)</Label>
                <Input
                  type="number"
                  value={availabilityForm.consultation_duration}
                  onChange={(e) => setAvailabilityForm({ ...availabilityForm, consultation_duration: parseInt(e.target.value) || 30 })}
                  className="w-24 mt-1"
                  min="10"
                  max="120"
                />
              </div>
              
              <div className="space-y-3">
                {Object.entries(dayLabels).map(([day, label]) => (
                  <div key={day} className="flex items-start gap-3 p-2 bg-slate-50 rounded-lg">
                    <span className="w-20 font-medium text-sm pt-2">{label}</span>
                    <div className="flex-1 space-y-2">
                      {(availabilityForm.consultation_slots[day] || []).map((slot, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={slot.start}
                            onChange={(e) => updateTimeSlot('consultation', day, idx, 'start', e.target.value)}
                            className="w-28"
                          />
                          <span>-</span>
                          <Input
                            type="time"
                            value={slot.end}
                            onChange={(e) => updateTimeSlot('consultation', day, idx, 'end', e.target.value)}
                            className="w-28"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeTimeSlot('consultation', day, idx)}
                          >
                            <X className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addTimeSlot('consultation', day)}
                        className="text-xs"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Ajouter
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="video" className="mt-4">
              <div className="mb-4">
                <Label className="text-sm">Durée d&apos;une téléconsultation (min)</Label>
                <Input
                  type="number"
                  value={availabilityForm.video_duration}
                  onChange={(e) => setAvailabilityForm({ ...availabilityForm, video_duration: parseInt(e.target.value) || 20 })}
                  className="w-24 mt-1"
                  min="10"
                  max="60"
                />
              </div>
              
              <div className="space-y-3">
                {Object.entries(dayLabels).map(([day, label]) => (
                  <div key={day} className="flex items-start gap-3 p-2 bg-purple-50 rounded-lg">
                    <span className="w-20 font-medium text-sm pt-2">{label}</span>
                    <div className="flex-1 space-y-2">
                      {(availabilityForm.video_slots[day] || []).map((slot, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={slot.start}
                            onChange={(e) => updateTimeSlot('video', day, idx, 'start', e.target.value)}
                            className="w-28"
                          />
                          <span>-</span>
                          <Input
                            type="time"
                            value={slot.end}
                            onChange={(e) => updateTimeSlot('video', day, idx, 'end', e.target.value)}
                            className="w-28"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeTimeSlot('video', day, idx)}
                          >
                            <X className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => addTimeSlot('video', day)}
                        className="text-xs"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Ajouter
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="flex gap-3 mt-6">
            <Button variant="outline" onClick={() => setShowAvailabilityModal(false)} className="flex-1">
              Annuler
            </Button>
            <Button onClick={saveAvailability} disabled={savingAvailability} className="flex-1 bg-red-500 hover:bg-red-600">
              {savingAvailability ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacilityProfessionals;
